import React from 'react'

import { Box, Link, List, ListItem, Typography, useTheme } from '@mui/material'
import { PRIVACY_POLICY_MARKDOWN, TERMS_AND_CONDITIONS_MARKDOWN } from '@repo/legal'
import ReactMarkdown from 'react-markdown'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { PRIVACY_URL, TERMS_URL } from 'constants/smsConsent'

type Props = {
  open: boolean
  onClose: () => void
  title: string
  url: string
}

/**
 * VERA-291 — shows a legal document (Terms and Conditions, Privacy Policy)
 * IN the app instead of opening a new tab to vera.inc. This app is white-
 * labeled for a company's own customers, and sending them to a `vera.inc`
 * tab tells them they're on Vera, not the brand they scanned a tag for.
 *
 * Renders the REAL, unmodified document text from `@repo/legal` — the
 * canonical Markdown source shared across apps (see that package's README
 * for the ownership model: those `.md` files are canonical, the public
 * vera.inc pages are derived copies). `TERMS_URL`/`PRIVACY_URL` (from
 * `constants/smsConsent.ts`, unchanged — still what the approved A2P
 * campaign cites) select which document to show.
 *
 * There is deliberately NO visible "also available at vera.inc/..." outbound
 * link in this drawer. That's the whole point of rendering it in-app rather
 * than an iframe/new tab (see above) — a visible vera.inc URL inside a
 * white-labeled certificate experience defeats the white-labeling entirely,
 * by telling the customer they're on Vera's own site. `TERMS_URL`/
 * `PRIVACY_URL` are still used below, just to pick which document's markdown
 * to render (and as the fallback link in the rare case a document isn't
 * available in-app at all) — never as a rendered "go to vera.inc" link.
 *
 * Previously this rendered `url` in an iframe. Two problems with that:
 * nobody had verified vera.inc permits framing (a `X-Frame-Options: DENY`
 * response would render blank), and it defeated white-labeling by loading a
 * vera.inc page inside a company-branded certificate experience. Rendering
 * from `@repo/legal` avoids both.
 *
 * Full-height (`fullScreen`), not the drawer default content-height — a
 * legal document needs the whole viewport to be readable/scrollable, unlike
 * the short confirms `DrawerPopup`'s content-height default is tuned for.
 */
export const LegalDocumentDrawer: React.FC<Props> = ({ open, onClose, title, url }) => {
  const theme = useTheme()

  const markdown =
    url === TERMS_URL
      ? TERMS_AND_CONDITIONS_MARKDOWN
      : url === PRIVACY_URL
        ? PRIVACY_POLICY_MARKDOWN
        : null

  return (
    <DrawerPopup
      title={title}
      handleOpen={open}
      handleClose={onClose}
      fullScreen
      contentSx={{ p: 0, display: 'flex', flexDirection: 'column' }}
      child={
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Typography
            // VERA-291: `body2` matches the description line every other
            // bottom drawer renders directly under its title (see
            // PhoneVerificationDrawer's/EditEmailDrawer's "Enter the N-digit
            // code we sent to ..." line) — `body4` isn't even defined in
            // this app's theme (only `@repo/ui`'s), so it silently fell
            // back to an unstyled inline element.
            variant='body2'
            // VERA-837: the description is fixed above a scrolling document,
            // so without a bottom border the body text slides under it with
            // nothing marking the boundary. Matches the drawer header's own
            // divider so the two read as one stacked chrome block.
            sx={{
              color: theme.palette.text.secondary,
              px: 2,
              py: 1.5,
              lineHeight: 1.5,
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            This is Vera's {title}, which governs your use of this certificate experience.
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 4 }}>
            {markdown ? (
              <ReactMarkdown
                components={{
                  // VERA-291: the theme's `h1`/`h4` variants are sized for
                  // this app's normal UI chrome (short labels, tight
                  // spaces), not a long-form legal document. `h4` in
                  // particular is 12px below the `sm` breakpoint — SMALLER
                  // than the 16px body text set below — so the numbered
                  // section headings ("1. Overview", "17. Contact") read as
                  // barely-there. Sizes are set explicitly here, local to
                  // this drawer's markdown mapping, to establish a real
                  // hierarchy: document title > section heading > 16px body.
                  h1: ({ children }) => (
                    <Typography variant='h1' sx={{ fontSize: '26px', lineHeight: 1.3, mb: 2 }}>
                      {children}
                    </Typography>
                  ),
                  h4: ({ children }) => (
                    <Typography
                      variant='h4'
                      sx={{
                        fontSize: '20px',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        color: theme.palette.text.primary,
                        mt: '32px',
                        mb: '12px',
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  p: ({ children }) => (
                    <Typography
                      variant='body2'
                      sx={{
                        color: theme.palette.text.secondary,
                        // VERA-291: body2 is 12px in this app's theme — too
                        // small for a long-form legal document read on a
                        // 375px phone. Bumped locally, in this drawer's own
                        // markdown mapping, rather than in the shared body2
                        // definition every other body2 usage in the app
                        // relies on.
                        fontSize: '16px',
                        lineHeight: 1.6,
                        mb: 2,
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  ul: ({ children }) => (
                    <List sx={{ listStyleType: 'disc', pl: 3, py: 0, mb: 2 }}>{children}</List>
                  ),
                  li: ({ children }) => (
                    <ListItem
                      disablePadding
                      sx={{ display: 'list-item', color: theme.palette.text.secondary }}
                    >
                      <Typography variant='body2' sx={{ fontSize: '16px', lineHeight: 1.6 }}>
                        {children}
                      </Typography>
                    </ListItem>
                  ),
                  a: ({ children, href }) => (
                    <Link href={href} target='_blank' rel='noopener noreferrer' color='inherit'>
                      {children}
                    </Link>
                  ),
                }}
              >
                {markdown}
              </ReactMarkdown>
            ) : (
              <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                This document isn't available in-app. Read it at{' '}
                <Link href={url} target='_blank' rel='noopener noreferrer' color='inherit'>
                  {url}
                </Link>
                .
              </Typography>
            )}
          </Box>
        </Box>
      }
    />
  )
}
